import { FilterRegularIcon } from "@elixir/icons";
import { AllQueuesData, QUEUE_TYPE } from "@elixir/types";
import { PropsWithChildren, useMemo } from "react";
import { InlineSearchComponentUi, Tabs } from "ui";
import useAllQueuesQuery from "../../apis/tryst/get-all-queues";
import { ReactComponent as PlusRegularIcon } from "../../assets/icons/font-awesome/plus-regular.svg";
import useQueueStore from "../../stores/queue-store";
import { TLeftQueuePanel } from "../../stores/queue-store/types";
import { getAptsForDoctor, getCount } from "../../utils/appointment-utils";
import { track } from "../../utils/tracking";
import AddNewPatient from "./add-new-patient";
import Booked from "./booked";
import Filter from "./filter";
import FollowUps from "./follow-ups";
import Others from "./others";
import { navigateLocal } from "./queue-header";
import { getIsEMRPlanExpired } from "../../molecules/tool-payments/common/utils";

const LeftQueuePanel = ({
  insideSidePanel = false,
}: {
  insideSidePanel?: boolean;
}) => {
  const { data } = useAllQueuesQuery();

  const leftPanelQueueType = useQueueStore((state) => state.leftPanelQueueType);
  const setLeftPanelQueueType = useQueueStore(
    (state) => state.setLeftPanelQueueType
  );
  const clinicId = useQueueStore((state) => state.clinicId);
  const rightPanelQueueType = useQueueStore(
    (state) => state.rightPanelQueueType
  );

  const isAddNewPatient = leftPanelQueueType === "add-new";

  const leftSearchQuery = useQueueStore((state) => state.leftSearchQuery);
  const setLeftSearchQuery = useQueueStore((state) => state.setLeftSearchQuery);

  const leftQueueFilter = useQueueStore((state) => state.leftQueueFilter);
  const setLeftQueueFilter = useQueueStore((state) => state.setLeftQueueFilter);

  const selectedDoctor = useQueueStore((state) => state.selectedDoctor);

  const filteredAptData = useMemo(
    () =>
      (Object.keys(data || {}) as (keyof AllQueuesData)[]).reduce(
        (acc, key) => {
          const filteredQueueDataByDoctor = getAptsForDoctor({
            apts: data?.[key] || [],
            doctor_oid: selectedDoctor?.oid || "",
          });
          return {
            ...acc,
            [key]: filteredQueueDataByDoctor,
          };
        },
        {} as AllQueuesData
      ),
    [data, selectedDoctor?.oid]
  );

  const isEMRPlanExpired = getIsEMRPlanExpired();

  return (
    <div
      className={`dr-rounded-t-8 ${
        isAddNewPatient ? (insideSidePanel ? "" : "dr-rounded-b-8") : ""
      } dr-relative dr-overflow-hidden dr-bg-darwin-touch-neutral-50 flex-1`}
      style={{
        height: `calc(100% - ${
          insideSidePanel ? "0rem" : isAddNewPatient ? "1rem" : "0rem"
        })`,
      }}
    >
      <Tabs<TLeftQueuePanel>
        onChange={(key) => {
          track("left_queue_panel", {
            tab: key,
          });
          setLeftPanelQueueType(key);
          navigateLocal(clinicId, key, rightPanelQueueType);
        }}
        className="dr-h-full dr-absolute dr-w-full dr-left-0"
        headerClassName="dr-flex dr-h-40 dr-items-center BodyBody2Regular dr-bg-doctor-darwin-accent-bright-blue-800 dr-px-8"
        headerStyle={
          isEMRPlanExpired
            ? {
                backgroundColor: "#647295",
              }
            : undefined
        }
        tabsContainerClassName="dr-space-x-8"
        activeClass="dr-flex dr-items-center dr-justify-center dr-px-8 dr-h-30 dr-rounded-4 dr-border-doctor-darwin-neutral-0 dr-bg-darwin-touch-neutral-0 dr-text-darwin-touch-primary-primary dr-border-transparent TouchFootnoteBold"
        inActiveClass="dr-text-darwin-touch-neutral-0 dr-opacity-70 TouchFootnoteRegular"
        activeKey={leftPanelQueueType}
        headerRightComponent={
          isAddNewPatient ? (
            <> </>
          ) : (
            <div
              className="dr-bg-doctor-darwin-accent-bright-blue-800 dr-h-40 dr-absolute dr-right-0 dr-flex dr-items-center dr-justify-center"
              style={
                isEMRPlanExpired
                  ? {
                      backgroundColor: "#647295",
                    }
                  : undefined
              }
            >
              <InlineSearchComponentUi
                hideText
                searchQuery={leftSearchQuery}
                setSearchQuery={setLeftSearchQuery}
              />

              <Filter
                setQueueFilter={setLeftQueueFilter}
                queueFilter={leftQueueFilter}
                queueType={leftPanelQueueType}
              >
                <div className="dr-flex dr-items-center dr-cursor-pointer dr-text-darwin-touch-neutral-0 dr-py-10 dr-pr-10 dr-pl-5">
                  <FilterRegularIcon />
                </div>
              </Filter>
            </div>
          )
        }
        items={[
          {
            children: (
              <LeftPanelHeightWrapper insideSidePanel={insideSidePanel}>
                <AddNewPatient />
              </LeftPanelHeightWrapper>
            ),
            Renderer: () => (
              <div
                className={`TouchFootnoteBold dr-flex dr-items-center dr-justify-center dr-space-x-8 dr-px-8 dr-h-30 dr-border-1 dr-cursor-pointer ${
                  !isAddNewPatient
                    ? "dr-text-doctor-darwin-neutral-0 dr-bg-transparent dr-border-b-doctor-darwin-neutral-0 dr-border-t-transparent dr-border-l-transparent dr-border-r-transparent"
                    : "dr-rounded-4 dr-border-doctor-darwin-neutral-0 dr-bg-darwin-touch-neutral-0 dr-text-darwin-touch-primary-primary dr-border-transparent"
                }`}
                onClick={() => setLeftPanelQueueType("add-new")}
              >
                <PlusRegularIcon className="dr-w-14 dr-h-14 dr-max-h-14 dr-max-w-14" />
                <div>New</div>
              </div>
            ),
            key: "add-new",
            label: "Add New",
          },
          {
            children: (
              <LeftPanelHeightWrapper insideSidePanel={insideSidePanel}>
                <Booked />
              </LeftPanelHeightWrapper>
            ),
            key: QUEUE_TYPE.BOOKED,
            label: `Booked (${getCount(filteredAptData?.bookedAppointments)})`,
          },
          {
            children: (
              <LeftPanelHeightWrapper insideSidePanel={insideSidePanel}>
                <FollowUps />
              </LeftPanelHeightWrapper>
            ),
            key: QUEUE_TYPE.FOLLOWUP,
            label: `Follow Ups (${getCount(
              filteredAptData?.followupAppointments
            )})`,
          },
          {
            children: (
              <LeftPanelHeightWrapper insideSidePanel={insideSidePanel}>
                <Others />
              </LeftPanelHeightWrapper>
            ),
            key: QUEUE_TYPE.OTHERS,
            label: `Others (${getCount(filteredAptData?.otherAppointments)})`,
          },
        ]}
        transition={false}
      />
    </div>
  );
};

export default LeftQueuePanel;

const LeftPanelHeightWrapper = ({
  insideSidePanel,
  children,
}: PropsWithChildren<{ insideSidePanel?: boolean }>) => {
  // const leftPanelQueueType = useQueueStore((state) => state.leftPanelQueueType);
  // const isAddNewPatient = leftPanelQueueType === "add-new";
  return (
    <div
      className="dr-w-full dr-overflow-auto dr-relative"
      style={{
        height: `calc(100% - 3.625rem)`,
      }}
    >
      {children}
    </div>
  );
};
